import React, { useRef, useEffect, useState } from "react";

const DemoVideo: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Ensure video plays on load and handles errors gracefully
    const handleCanPlay = () => {
      setIsLoaded(true);
      video.play().catch((error) => {
        // Silently handle autoplay restrictions (common on mobile)
        if (error.name !== "NotAllowedError") {
          console.warn("Video autoplay prevented:", error);
        }
      });
    };

    const handleLoadedData = () => {
      setIsLoaded(true);
      video.play().catch(() => {
        // Silently handle autoplay restrictions
      });
    };

    const handleError = (e: Event) => {
      console.error("Video failed to load:", e);
      setIsLoaded(false);
    };

    // Ensure loop works properly
    const handleEnded = () => {
      video.currentTime = 0;
      video.play().catch(() => {
        // Silently handle autoplay restrictions
      });
    };

    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("loadeddata", handleLoadedData);
    video.addEventListener("error", handleError);
    video.addEventListener("ended", handleEnded);

    // Try to play immediately if already loaded
    if (video.readyState >= 3) {
      setIsLoaded(true);
      video.play().catch(() => {
        // Silently handle autoplay restrictions
      });
    }

    return () => {
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("loadeddata", handleLoadedData);
      video.removeEventListener("error", handleError);
      video.removeEventListener("ended", handleEnded);
    };
  }, []);

  return (
    <section className="w-full pt-0 sm:pt-12 pb-12 sm:pb-16 lg:pb-20 relative overflow-hidden -mt-6 sm:mt-0">
      {/* Background gradient - matching ProblemSolution */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/10" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Video Container */}
          <div className="relative w-full max-w-6xl mx-auto">
            <div className="relative rounded-xl sm:rounded-2xl lg:rounded-3xl overflow-hidden border border-border/30 bg-background/50 backdrop-blur-sm" style={{
              boxShadow: '0 10px 40px -10px rgba(0, 0, 0, 0.1), 0 4px 20px -4px rgba(0, 0, 0, 0.08)'
            }}>
              {/* Video Element */}
              <video
                ref={videoRef}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                className="w-full h-auto block"
                aria-label="Seoro platform demo video"
                style={{
                  opacity: isLoaded ? 1 : 0,
                  transition: "opacity 0.3s ease-in-out",
                }}
              >
                <source src="/demo2.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              {/* Loading placeholder - only shown while video is loading */}
              {!isLoaded && (
                <div className="absolute inset-0 w-full h-full bg-muted/20 flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DemoVideo;

